import { useState, useEffect, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useMoneda } from '../context/MonedaContext'

const CAMPOS_LABEL = {
  nombre: 'Nombre', codigo: 'Código', descripcion: 'Descripción',
  precio_compra: 'Precio Compra', precio_venta: 'Precio Venta',
  precio_mayorista: 'Precio Mayorista', cantidad_mayorista: 'Cantidad Mínima Mayorista',
  stock_actual: 'Stock Actual', stock_minimo: 'Stock Mínimo', activo: 'Estado',
}

const FORM_INICIAL = {
  nombre: '', codigo: '', precio_compra: '', precio_venta: '',
  precio_mayorista: '', cantidad_mayorista: '6',
  stock_actual: '', stock_minimo: '', descripcion: '',
}

const AJUSTE_INICIAL = { tipo: 'entrada', cantidad: '', motivo: '', proveedor_id: '' }

const S = {
  centered: { textAlign: 'center', padding: '3rem', color: 'var(--text-light)' },
  emptyMsg: { color: 'var(--text-light)', padding: '2rem', textAlign: 'center' },
  emptyRow: { textAlign: 'center', padding: '2rem', color: 'var(--text-light)' },
  nombreCell: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  nombreText: { fontWeight: '500' },
  expandBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '0.75rem', color: 'var(--text-light)', padding: '0.1rem 0.3rem',
  },
  precioCompra: { color: 'var(--text-light)' },
  stockCell: { fontWeight: '600' },
  badgeOk: { background: '#d1fae5', color: '#065f46' },
  accionesWrap: { display: 'flex', gap: '0.4rem' },
  btnMini: { padding: '0.4rem 0.75rem', fontSize: '0.8rem' },
  btnAjustar: { padding: '0.4rem 0.75rem', fontSize: '0.8rem', color: '#10b981', borderColor: '#10b981' },
  expandTd: { padding: 0, background: '#f8fafc' },
  expandInner: { padding: '1.25rem 1.5rem', borderTop: '2px solid var(--primary)', borderBottom: '1px solid var(--border)' },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.25rem' },
  detailLabel: { fontSize: '0.75rem', color: 'var(--text-light)', fontWeight: '600', marginBottom: '0.25rem' },
  detailValue: { fontSize: '0.875rem' },
  detailMuted: { color: 'var(--text-light)' },
  margen: { fontSize: '0.875rem', fontWeight: '600', color: '#10b981' },
  histLabel: { fontSize: '0.75rem', color: 'var(--text-light)', fontWeight: '600', marginBottom: '0.75rem' },
  histMuted: { fontSize: '0.875rem', color: 'var(--text-light)' },
  histList: { display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '200px', overflowY: 'auto' },
  histRow: { display: 'flex', gap: '0.75rem', alignItems: 'baseline', fontSize: '0.8rem', padding: '0.4rem 0', borderBottom: '1px solid var(--border)' },
  histTime: { color: 'var(--text-light)', whiteSpace: 'nowrap' },
  histSep: { color: 'var(--text-light)' },
  histUser: { fontWeight: '600' },
  histAntes: { color: '#ef4444', textDecoration: 'line-through' },
  histDespues: { color: '#10b981', fontWeight: '600' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  ajusteInfo: { fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '1rem' },
  tipoWrap: { display: 'flex', gap: '0.5rem' },
  resultado: { padding: '0.75rem', background: '#f8fafc', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.875rem' },
  labelOpcional: { color: 'var(--text-light)', fontWeight: '400' },
}

const tipoBtnStyle = (activo, tipo) => ({
  flex: 1, padding: '0.6rem',
  border: `2px solid ${activo ? (tipo === 'entrada' ? '#10b981' : '#ef4444') : 'var(--border)'}`,
  borderRadius: '0.5rem',
  background: activo ? (tipo === 'entrada' ? '#ecfdf5' : '#fef2f2') : 'white',
  cursor: 'pointer', fontWeight: activo ? '600' : '400',
})

export default function Inventario({ user, onNavigate }) {
  const navigate = useNavigate()
  const { perfil, sucursalActiva, permisos } = useAuth()
  const { formatearMoneda } = useMoneda()
  const rol = perfil?.rol || 'admin'

  const [productos, setProductos] = useState([])
  const [loading, setLoading] = useState(true)
  const [busqueda, setBusqueda] = useState('')
  const [productoExpandido, setProductoExpandido] = useState(null)
  const [auditoriaProducto, setAuditoriaProducto] = useState({})
  const [modalNuevo, setModalNuevo] = useState(false)
  const [modalEditar, setModalEditar] = useState(null)
  const [modalAjuste, setModalAjuste] = useState(null)
  const [formAjuste, setFormAjuste] = useState(AJUSTE_INICIAL)
  const [proveedores, setProveedores] = useState([])
  const [formData, setFormData] = useState(FORM_INICIAL)

  useEffect(() => {
    cargarProductos()
    cargarProveedores()
  }, [sucursalActiva?.id])

  const cargarProductos = async () => {
    setLoading(true)
    try {
      const sucursalId = sucursalActiva?.id || '1'
      const data = await apiFetch(`/inventario/productos?sucursal_id=${sucursalId}&activo=true`)
      setProductos(Array.isArray(data) ? data : [])
    } catch (err) {
      console.warn('No se pudieron obtener productos del backend:', err)
      setProductos([])
    } finally {
      setLoading(false)
    }
  }

  const cargarProveedores = async () => {
    try {
      const data = await apiFetch('/contactos?tipo=proveedor&activo=true')
      if (Array.isArray(data)) setProveedores(data)
    } catch (err) {
      console.warn('No se pudieron obtener proveedores:', err)
      setProveedores([])
    }
  }

  const cargarAuditoria = async (productoId) => {
    if (auditoriaProducto[productoId]) return
    try {
      const data = await apiFetch(`/productos/${productoId}/auditoria?limit=20`)
      setAuditoriaProducto(prev => ({ ...prev, [productoId]: Array.isArray(data) ? data : [] }))
    } catch (err) {
      setAuditoriaProducto(prev => ({ ...prev, [productoId]: [] }))
    }
  }

  const toggleExpandir = (id) => {
    if (productoExpandido === id) {
      setProductoExpandido(null)
    } else {
      setProductoExpandido(id)
      cargarAuditoria(id)
    }
  }

  const handleNuevo = async (e) => {
    e.preventDefault()
    const precioVenta = Number(formData.precio_venta)
    if (!Number.isFinite(precioVenta) || precioVenta < 0) {
      alert('Precio de venta inválido')
      return
    }
    const nuevo = {
      id: `prod-${Date.now()}`,
      nombre: formData.nombre,
      codigo: formData.codigo || null,
      descripcion: formData.descripcion || null,
      precio_compra: Number(formData.precio_compra) || 0,
      precio_venta: precioVenta,
      precio_mayorista: formData.precio_mayorista ? Number(formData.precio_mayorista) : null,
      cantidad_mayorista: parseInt(formData.cantidad_mayorista, 10) || 6,
      stock_actual: parseInt(formData.stock_actual, 10) || 0,
      stock_minimo: parseInt(formData.stock_minimo, 10) || 0,
      activo: true,
      created_at: new Date().toISOString()
    }
    try {
      await apiFetch('/productos', { method: 'POST', body: JSON.stringify(nuevo) })
    } catch (err) {
      console.warn('Guardando producto localmente:', err)
    }
    setProductos([nuevo, ...productos])
    setModalNuevo(false)
    resetForm()
  }

  const abrirEditar = (producto) => {
    setModalEditar(producto)
    setFormData({
      nombre: producto.nombre,
      codigo: producto.codigo || '',
      precio_compra: producto.precio_compra ?? '',
      precio_venta: producto.precio_venta,
      precio_mayorista: producto.precio_mayorista ?? '',
      cantidad_mayorista: producto.cantidad_mayorista ?? 6,
      stock_actual: producto.stock_actual,
      stock_minimo: producto.stock_minimo,
      descripcion: producto.descripcion || '',
    })
  }

  const handleEditar = async (e) => {
    e.preventDefault()
    const p = modalEditar
    if (!p?.id) return
    const precioVenta = Number(formData.precio_venta)
    if (!Number.isFinite(precioVenta) || precioVenta < 0) {
      alert('Precio de venta inválido')
      return
    }
    const actualizados = productos.map(item => item.id === p.id ? {
      ...item,
      nombre: formData.nombre,
      codigo: formData.codigo || null,
      descripcion: formData.descripcion || null,
      precio_compra: Number(formData.precio_compra) || 0,
      precio_venta: precioVenta,
      precio_mayorista: formData.precio_mayorista ? Number(formData.precio_mayorista) : null,
      cantidad_mayorista: parseInt(formData.cantidad_mayorista, 10) || 6,
      stock_minimo: parseInt(formData.stock_minimo, 10) || 0
    } : item)

    try {
      await apiFetch(`/productos/${p.id}`, { method: 'PATCH', body: JSON.stringify(formData) })
    } catch (err) {
      console.warn('Editando producto en memoria local:', err)
    }

    setProductos(actualizados)
    setModalEditar(null)
    resetForm()
  }

  const handleAjuste = async (e) => {
    e.preventDefault()
    const p = modalAjuste
    if (!p?.id) return
    const cantidad = parseInt(formAjuste.cantidad, 10)
    if (!Number.isFinite(cantidad) || cantidad <= 0) return

    const factor = formAjuste.tipo === 'entrada' ? 1 : -1
    const nuevoStock = Math.max(0, p.stock_actual + (cantidad * factor))

    setProductos(productos.map(item => item.id === p.id ? { ...item, stock_actual: nuevoStock } : item))
    setModalAjuste(null)
    setFormAjuste(AJUSTE_INICIAL)
  }

  const toggleActivo = (producto) => {
    const nuevoEstado = !producto.activo
    setProductos(productos.map(p => p.id === producto.id ? { ...p, activo: nuevoEstado } : p))
  }

  const resetForm = () => setFormData(FORM_INICIAL)

  const formatearFecha = (f) =>
    new Date(f).toLocaleString('es-CL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })

  const productosFiltrados = productos.filter(p =>
    p.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    p.codigo?.toLowerCase().includes(busqueda.toLowerCase())
  )

  const puedeVerCosto = rol === 'admin' || rol === 'almacenista'
  const puedoGestionar = permisos?.gestionar_inventario !== false
  const puedoAjustar = permisos?.ajustar_stock !== false
  const colSpan = puedoGestionar ? (puedeVerCosto ? 8 : 7) : (puedeVerCosto ? 7 : 6)

  const renderBadge = (p) => {
    if (p.activo === false) return <span className="badge badge-danger">Inactivo</span>
    if (p.stock_actual <= 0) return <span className="badge badge-danger">Sin stock</span>
    if (p.stock_actual <= p.stock_minimo) return <span className="badge badge-warning">Stock bajo</span>
    return <span className="badge" style={S.badgeOk}>Disponible</span>
  }

  if (loading) return (
    <div className="main-content">
      <div className="page-header"><h1 className="page-title">Inventario</h1></div>
      <div style={S.centered}>Cargando inventario...</div>
    </div>
  )

  return (
    <div className="main-content">
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h1 className="page-title">Inventario</h1>
            <p className="page-subtitle">{productos.length} producto(s) en catálogo — {sucursalActiva?.nombre || 'Casa Matriz'}</p>
          </div>
          {puedoGestionar && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary" onClick={() => setModalNuevo(true)}>
                + Nuevo Producto
              </button>
              <button className="btn btn-outline" onClick={() => onNavigate && onNavigate('facturas')}>
                + Ingreso por Factura
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="search-box">
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por nombre o código..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Precio Venta</th>
                {puedeVerCosto && <th>Precio Compra</th>}
                <th>Stock</th>
                <th>Stock Min.</th>
                <th>Estado</th>
                {puedoGestionar && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {productosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} style={S.emptyRow}>
                    {busqueda ? 'No se encontraron productos coincidentes' : 'No hay productos en el catálogo de esta sucursal'}
                  </td>
                </tr>
              ) : productosFiltrados.map((p) => (
                <Fragment key={p.id}>
                  <tr style={{ opacity: p.activo === false ? 0.5 : 1 }}>
                    <td>{p.codigo || '-'}</td>
                    <td>
                      <div style={S.nombreCell}>
                        <span style={S.nombreText}>{p.nombre}</span>
                        <button onClick={() => toggleExpandir(p.id)} style={S.expandBtn}>
                          {productoExpandido === p.id ? 'menos' : 'más'}
                        </button>
                      </div>
                    </td>
                    <td>{formatearMoneda(p.precio_override ?? p.precio_venta)}</td>
                    {puedeVerCosto && (
                      <td style={S.precioCompra}>{formatearMoneda(p.precio_compra || 0)}</td>
                    )}
                    <td style={S.stockCell}>{p.stock_actual}</td>
                    <td>{p.stock_minimo}</td>
                    <td>{renderBadge(p)}</td>
                    {puedoGestionar && (
                      <td>
                        <div style={S.accionesWrap}>
                          <button className="btn btn-outline" onClick={() => abrirEditar(p)} style={S.btnMini}>
                            Editar
                          </button>
                          {puedoAjustar && (
                            <button
                              className="btn btn-outline"
                              onClick={() => {
                                setModalAjuste(p)
                                setFormAjuste(AJUSTE_INICIAL)
                              }}
                              style={S.btnAjustar}
                            >
                              Ajustar
                            </button>
                          )}
                          {rol === 'admin' && (
                            <button
                              className={`btn ${p.activo === false ? 'btn-primary' : 'btn-outline'}`}
                              onClick={() => toggleActivo(p)}
                              style={S.btnMini}
                            >
                              {p.activo === false ? 'Activar' : 'Desactivar'}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>

                  {productoExpandido === p.id && (
                    <tr>
                      <td colSpan={colSpan} style={S.expandTd}>
                        <div style={S.expandInner}>
                          <div style={S.detailGrid}>
                            <div>
                              <div style={S.detailLabel}>DESCRIPCIÓN</div>
                              <div style={S.detailValue}>
                                {p.descripcion || <span style={S.detailMuted}>Sin descripción</span>}
                              </div>
                            </div>
                            <div>
                              <div style={S.detailLabel}>CREADO</div>
                              <div style={S.detailValue}>{p.created_at ? formatearFecha(p.created_at) : '-'}</div>
                            </div>
                            {puedeVerCosto && (
                              <div>
                                <div style={S.detailLabel}>MARGEN</div>
                                <div style={S.margen}>
                                  {p.precio_compra > 0
                                    ? `${Math.round(((p.precio_venta - p.precio_compra) / p.precio_compra) * 100)}%`
                                    : 'N/A'}
                                </div>
                              </div>
                            )}
                          </div>

                          <div>
                            <div style={S.histLabel}>HISTORIAL DE CAMBIOS</div>
                            {!auditoriaProducto[p.id] ? (
                              <div style={S.histMuted}>Sin cambios recientes registrados</div>
                            ) : auditoriaProducto[p.id].length === 0 ? (
                              <div style={S.histMuted}>Sin cambios registrados</div>
                            ) : (
                              <div style={S.histList}>
                                {auditoriaProducto[p.id].map((a) => (
                                  <div key={a.id} style={S.histRow}>
                                    <span style={S.histTime}>{formatearFecha(a.created_at)}</span>
                                    <span style={S.histSep}>·</span>
                                    <span style={S.histUser}>{a.perfiles?.nombre || 'Sistema'}</span>
                                    <span style={S.histSep}>·</span>
                                    <span><strong>{CAMPOS_LABEL[a.campo] || a.campo}</strong>: </span>
                                    <span style={S.histAntes}>{a.valor_anterior || 'vacío'}</span>
                                    <span>→</span>
                                    <span style={S.histDespues}>{a.valor_nuevo}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalNuevo && (
        <div className="modal-overlay" onClick={() => { setModalNuevo(false); resetForm() }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Nuevo Producto</h2></div>
            <form onSubmit={handleNuevo}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input type="text" className="form-input" required
                  value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Código / SKU</label>
                <input type="text" className="form-input"
                  value={formData.codigo} onChange={e => setFormData({ ...formData, codigo: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Descripción</label>
                <textarea className="form-input" rows="2"
                  value={formData.descripcion} onChange={e => setFormData({ ...formData, descripcion: e.target.value })} />
              </div>
              <div style={S.grid2}>
                <div className="form-group">
                  <label className="form-label">Precio Compra</label>
                  <input type="number" step="1" min="0" className="form-input"
                    value={formData.precio_compra} onChange={e => setFormData({ ...formData, precio_compra: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Precio Venta *</label>
                  <input type="number" step="1" min="0" className="form-input" required
                    value={formData.precio_venta} onChange={e => setFormData({ ...formData, precio_venta: e.target.value })} />
                </div>
              </div>
              <div style={S.grid2}>
                <div className="form-group">
                  <label className="form-label">Precio Mayorista</label>
                  <input type="number" step="1" min="0" className="form-input"
                    value={formData.precio_mayorista} onChange={e => setFormData({ ...formData, precio_mayorista: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Cantidad Mínima Mayorista</label>
                  <input type="number" step="1" min="1" className="form-input"
                    value={formData.cantidad_mayorista} onChange={e => setFormData({ ...formData, cantidad_mayorista: e.target.value })} />
                </div>
              </div>
              <div style={S.grid2}>
                <div className="form-group">
                  <label className="form-label">Stock Inicial</label>
                  <input type="number" min="0" className="form-input"
                    value={formData.stock_actual} onChange={e => setFormData({ ...formData, stock_actual: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Stock Mínimo</label>
                  <input type="number" min="0" className="form-input"
                    value={formData.stock_minimo} onChange={e => setFormData({ ...formData, stock_minimo: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => { setModalNuevo(false); resetForm() }}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar Producto</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalEditar && (
        <div className="modal-overlay" onClick={() => { setModalEditar(null); resetForm() }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Editar: {modalEditar.nombre}</h2></div>
            <form onSubmit={handleEditar}>
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input type="text" className="form-input" required
                  value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Código / SKU</label>
                <input type="text" className="form-input"
                  value={formData.codigo} onChange={e => setFormData({ ...formData, codigo: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Descripción</label>
                <textarea className="form-input" rows="2"
                  value={formData.descripcion} onChange={e => setFormData({ ...formData, descripcion: e.target.value })} />
              </div>
              <div style={S.grid2}>
                <div className="form-group">
                  <label className="form-label">Precio Compra</label>
                  <input type="number" step="1" min="0" className="form-input"
                    value={formData.precio_compra} onChange={e => setFormData({ ...formData, precio_compra: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Precio Venta *</label>
                  <input type="number" step="1" min="0" className="form-input" required
                    value={formData.precio_venta} onChange={e => setFormData({ ...formData, precio_venta: e.target.value })} />
                </div>
              </div>
              <div style={S.grid2}>
                <div className="form-group">
                  <label className="form-label">Precio Mayorista</label>
                  <input type="number" step="1" min="0" className="form-input"
                    value={formData.precio_mayorista} onChange={e => setFormData({ ...formData, precio_mayorista: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Cantidad Mínima Mayorista</label>
                  <input type="number" step="1" min="1" className="form-input"
                    value={formData.cantidad_mayorista} onChange={e => setFormData({ ...formData, cantidad_mayorista: e.target.value })} />
                </div>
              </div>
              <div style={S.grid2}>
                <div className="form-group">
                  <label className="form-label">Stock Mínimo</label>
                  <input type="number" min="0" className="form-input"
                    value={formData.stock_minimo} onChange={e => setFormData({ ...formData, stock_minimo: e.target.value })} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => { setModalEditar(null); resetForm() }}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar Cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalAjuste && (
        <div className="modal-overlay" onClick={() => setModalAjuste(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Ajustar Stock: {modalAjuste.nombre}</h2>
            </div>
            <p style={S.ajusteInfo}>
              Stock actual: <strong>{modalAjuste.stock_actual}</strong> unidades
            </p>
            <form onSubmit={handleAjuste}>
              <div className="form-group">
                <label className="form-label">Tipo de ajuste</label>
                <div style={S.tipoWrap}>
                  {['entrada', 'salida'].map(t => (
                    <button key={t} type="button"
                      onClick={() => setFormAjuste({ ...formAjuste, tipo: t, proveedor_id: '' })}
                      style={tipoBtnStyle(formAjuste.tipo === t, t)}>
                      {t === 'entrada' ? 'Entrada' : 'Salida'}
                    </button>
                  ))}
                </div>
              </div>
              {formAjuste.tipo === 'entrada' && proveedores.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Proveedor <span style={S.labelOpcional}>(opcional)</span></label>
                  <select className="form-input" value={formAjuste.proveedor_id}
                    onChange={e => setFormAjuste({ ...formAjuste, proveedor_id: e.target.value })}>
                    <option value="">— Sin proveedor —</option>
                    {proveedores.map(p => (
                      <option key={p.id} value={p.id}>{p.razon_social || p.nombre}{p.rut ? ` — ${p.rut}` : ''}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Cantidad *</label>
                <input type="number" className="form-input" min="1" required placeholder="Ej: 10"
                  value={formAjuste.cantidad} onChange={e => setFormAjuste({ ...formAjuste, cantidad: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Motivo</label>
                <input type="text" className="form-input" placeholder="Ej: Compra a proveedor, merma, etc."
                  value={formAjuste.motivo} onChange={e => setFormAjuste({ ...formAjuste, motivo: e.target.value })} />
              </div>
              {formAjuste.cantidad && (
                <div style={S.resultado}>
                  Stock resultante: <strong>{formAjuste.tipo === 'entrada'
                    ? modalAjuste.stock_actual + (parseInt(formAjuste.cantidad, 10) || 0)
                    : Math.max(0, modalAjuste.stock_actual - (parseInt(formAjuste.cantidad, 10) || 0))}</strong> unidades
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setModalAjuste(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Confirmar Ajuste</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}